import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface MetaListItemProps extends HTMLAttributes<HTMLLIElement> {
  /**
   * Right-aligned content (typically a `Badge` or status pill). When
   * provided, the children flex container truncates to leave space for
   * the trailing slot.
   */
  trailing?: ReactNode;
  /**
   * Vertical alignment of the row's children relative to `trailing`.
   * Defaults to 'center'. Use 'start' for multi-line content (feature
   * bullets, long captions) so the trailing slot aligns to the top.
   */
  align?: 'center' | 'start';
  /**
   * Background variant. 'default' uses `bg-card`, 'subtle' uses
   * `bg-card/60` for a softer feel inside dense category lists.
   */
  variant?: 'default' | 'subtle';
  children: ReactNode;
  className?: string;
}

/**
 * Compact row used inside detail-page lists ("Requires", "Required by",
 * contributions, capabilities). Provides the consistent rounded-card
 * frame, padding, and left-content / trailing-badge layout so every
 * list on the same page looks the same without re-implementing the
 * styling each time.
 *
 * Children render on the left. The optional `trailing` slot is pinned
 * to the right. The component renders an `<li>` so it composes with
 * `<ul>` parents — wrap inside a flex/grid container if you need a
 * non-list layout.
 */
export function MetaListItem({
  trailing,
  align = 'center',
  variant = 'default',
  className,
  children,
  ...rest
}: MetaListItemProps) {
  return (
    <li
      className={cn(
        'flex gap-2 rounded-md border border-border/40 px-3 py-2',
        align === 'center' ? 'items-center' : 'items-start',
        variant === 'subtle' ? 'bg-card/60' : 'bg-card',
        className,
      )}
      {...rest}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 gap-2',
          align === 'center' ? 'items-center' : 'items-start',
        )}
      >
        {children}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-1.5">{trailing}</div>}
    </li>
  );
}
